import axios from 'axios';

const SESSION_KEY = 'jb_dek';
const PRF_WRAP_PREFIX = 'jb_prf_wrap_';

// --- Session DEK (base64 raw key bytes) ---

export function getSessionDek() {
  return sessionStorage.getItem(SESSION_KEY);
}

export function setSessionDek(dekBase64) {
  sessionStorage.setItem(SESSION_KEY, dekBase64);
}

export function clearSessionDek() {
  sessionStorage.removeItem(SESSION_KEY);
}

export function hasSessionDek() {
  return !!sessionStorage.getItem(SESSION_KEY);
}

export async function getSessionDekAsCryptoKey() {
  const b64 = getSessionDek();
  if (!b64) return null;
  const raw = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

// --- Generate a fresh DEK ---

export async function generateDek() {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const raw = await crypto.subtle.exportKey('raw', key);
  return btoa(String.fromCharCode(...new Uint8Array(raw)));
}

// --- Legacy migration helpers ---

async function hashEmail(email) {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Returns the legacy bare DEK (base64) from old storage, or null if not found.
// Checks localStorage first (post-logout path), then sessionStorage (active-session path).
export async function findLegacyDek(userEmail) {
  const emailHash = await hashEmail(userEmail);
  const fromLocalStorage = localStorage.getItem('jb_enc_key_' + emailHash);
  if (fromLocalStorage) return { dek: fromLocalStorage, source: 'localStorage', emailHash };
  const fromSessionStorage = sessionStorage.getItem('jb_enc_key');
  if (fromSessionStorage) return { dek: fromSessionStorage, source: 'sessionStorage', emailHash };
  return null;
}

export async function removeLegacyKeys(emailHash) {
  localStorage.removeItem('jb_enc_key_' + emailHash);
  sessionStorage.removeItem('jb_enc_key');
}

// --- PRF local cache ---

export function getPrfWrap(credentialId) {
  const raw = localStorage.getItem(PRF_WRAP_PREFIX + credentialId);
  return raw ? JSON.parse(raw) : null;
}

export function setPrfWrap(credentialId, wrap) {
  localStorage.setItem(PRF_WRAP_PREFIX + credentialId, JSON.stringify(wrap));
}

export function clearPrfWrap(credentialId) {
  localStorage.removeItem(PRF_WRAP_PREFIX + credentialId);
}

// --- PRF crypto ---

// Converts base64 or base64url string to Uint8Array
export function decodeBase64(b64) {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (padded.length % 4)) % 4;
  return Uint8Array.from(atob(padded + '='.repeat(padding)), c => c.charCodeAt(0));
}

async function deriveKekFromPrf(prfOutputB64, prfSaltB64) {
  const prfBytes = decodeBase64(prfOutputB64);
  const saltBytes = decodeBase64(prfSaltB64);
  const baseKey = await crypto.subtle.importKey('raw', prfBytes, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: saltBytes, info: new TextEncoder().encode('jurybox-dek-wrap') },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function wrapDekWithPrf(dekBase64, prfOutputB64, prfSaltB64) {
  const kek = await deriveKekFromPrf(prfOutputB64, prfSaltB64);
  const dekBytes = decodeBase64(dekBase64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, kek, dekBytes);
  return {
    ciphertext: btoa(String.fromCharCode(...new Uint8Array(wrapped))),
    iv: btoa(String.fromCharCode(...iv)),
    prfSalt: prfSaltB64,
  };
}

export async function unwrapDekWithPrf(wrap, prfOutputB64) {
  const kek = await deriveKekFromPrf(prfOutputB64, wrap.prfSalt);
  const iv = decodeBase64(wrap.iv);
  const ciphertext = decodeBase64(wrap.ciphertext);
  const dekBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, kek, ciphertext);
  return btoa(String.fromCharCode(...new Uint8Array(dekBytes)));
}

// --- Server wrap/unwrap ---

export async function serverWrapDek(dekBase64, overwrite = false) {
  await axios.post('/api/keys/wrap', { dek: dekBase64, overwrite }, { withCredentials: true });
}

export async function serverUnwrapDek() {
  const res = await axios.post('/api/keys/unwrap', {}, { withCredentials: true });
  return res.data.dek;
}

export async function getKeyStatus() {
  const res = await axios.get('/api/keys/status', { withCredentials: true });
  return res.data;
}

// --- Main unlock ---
// prfOutput: base64url string from clientExtensionResults.prf.results.first, or null
// prfCredentialId: credential ID string, or null
// prfWrappedDek: { ciphertext, iv } from server auth/verify response, or null
// prfSalt: base64 from server, or null
// Returns base64 DEK and stores in sessionStorage.

export async function unlockDek({ prfOutput = null, prfCredentialId = null, prfWrappedDek = null, prfSalt = null } = {}) {
  if (prfOutput && prfCredentialId) {
    let wrap = getPrfWrap(prfCredentialId);

    if (!wrap && prfWrappedDek && prfSalt) {
      wrap = { ...prfWrappedDek, prfSalt };
      setPrfWrap(prfCredentialId, wrap);
    }

    if (wrap) {
      const dek = await unwrapDekWithPrf(wrap, prfOutput);
      setSessionDek(dek);
      return dek;
    }
    // PRF output present but no wrap yet — fall through to server unwrap,
    // then ensureEncryptionKey registers the PRF wrap for next time.
  }

  const dek = await serverUnwrapDek();
  setSessionDek(dek);
  return dek;
}
